import React, { useState } from "react";
import "./PGAModelBuilderOutput.css";
import { graphql, useLazyLoadQuery } from "react-relay";
import StatTable from "../../../../../StatTable";
import { colorGradient, sortModelValues } from "../../../../../../Utils";
import Button from "../../../../../Button";

const GET_OUTPUT = graphql`
  query PGAModelBuilderOutputQuery($modelID: ID) {
    pgaModelOutput(modelId: $modelID) {
      columnNames
      rows {
        player {
          id
          fullName
          firstName
          lastName
          nameDisplay
          nameShort
          dkPlayer {
            id
            name
          }
        }
        salary
        totalModelScore
        totalModelRank
        values {
          score
          stat
          rank
          featureName
        }
      }
    }
  }
`;

export default function PGAModelBuilderOutput(props) {
  const queryData = useLazyLoadQuery(
    GET_OUTPUT,
    {
      modelID: props.modelID,
    },
    { fetchKey: props.outputReloads, fetchPolicy: "network-only" }
  );

  const [minMaxVals, setMinMaxVals] = useState({});

  let data = React.useMemo(() => {
    if (!queryData.pgaModelOutput) {
      return null;
    }

    let localMinMax = {
      Total: { min: Number.MAX_SAFE_INTEGER, max: Number.MIN_SAFE_INTEGER },
    };

    queryData.pgaModelOutput.columnNames.forEach((columnName) => {
      localMinMax[columnName] = {
        min: Number.MAX_SAFE_INTEGER,
        max: Number.MIN_SAFE_INTEGER,
      };
    });

    return queryData.pgaModelOutput.rows.map((row) => {
      const newObj = { ...row };

      newObj.player = row.player.firstName + " " + row.player.lastName;

      if (row.player.dkPlayer) {
        newObj.dkPlayerId = row.player.dkPlayer.id;
      }

      let totalVal;

      if (props.viewRanks) {
        totalVal = row.totalModelRank;
      } else {
        totalVal = row.totalModelScore;
      }

      if (totalVal > localMinMax["Total"]["max"]) {
        localMinMax["Total"]["max"] = totalVal;
      }

      if (totalVal < localMinMax["Total"]["min"]) {
        localMinMax["Total"]["min"] = totalVal;
      }

      if (totalVal === null) {
        totalVal = "-";
      }

      newObj.totalModelScore = totalVal;

      row.values.map((val, index) => {
        let desiredVal;

        if (props.viewRanks) {
          desiredVal = val.rank;
        } else {
          desiredVal = val.score;
        }

        const columnName = val.featureName;

        if (desiredVal > localMinMax[columnName]["max"]) {
          localMinMax[columnName]["max"] = desiredVal;
        }

        if (desiredVal < localMinMax[columnName]["min"]) {
          localMinMax[columnName]["min"] = desiredVal;
        }

        if (desiredVal === null) {
          desiredVal = "-";
        }

        newObj[`stat${index}`] = desiredVal;
      });

      setMinMaxVals(localMinMax);

      return newObj;
    });
  }, [props.outputReloads, props.viewRanks]);

  let columns = React.useMemo(() => {
    if (!queryData.pgaModelOutput) {
      return null;
    }

    const statCols = queryData.pgaModelOutput.columnNames.map(
      (column, index) => {
        return {
          Header: column,
          accessor: `stat${index}`,
          sortType: sortModelValues,
        };
      }
    );

    return [
      {
        Header: "Name",
        accessor: "player",
      },
      {
        Header: "Salary",
        accessor: "salary",
      },
      {
        Header: "Total",
        accessor: "totalModelScore",
        sortType: sortModelValues,
      },
      ...statCols,
    ];
  }, [props.outputReloads]);

  if (!data) {
    return <div>Build model to see output.</div>;
  }

  const tableMemo = React.useMemo(() => {
    return (
      <StatTable
        data={data}
        columns={columns}
        showDKPlayerInfo={props.showDKPlayerInfo}
        heatMap={true}
        paginate={false}
        getCellProps={(cellInfo) => {
          if (
            !(cellInfo.column.Header === "Name") &&
            !(cellInfo.column.Header === "Salary")
          ) {
            const headerName = cellInfo.column.Header;
            if (!(headerName in minMaxVals)) {
              return {};
            }

            const cellValue = cellInfo.value;
            const columnMin = minMaxVals[headerName]["min"];
            const columnMax = minMaxVals[headerName]["max"];
            const columnDiff = columnMax - columnMin;
            const valueNormalized = (cellValue - columnMin) / columnDiff;

            let highStatIsGood = !props.viewRanks;

            if (headerName === "Total" && props.viewRanks) {
              highStatIsGood = false;
            }

            const valueColor = colorGradient(valueNormalized, highStatIsGood);

            return { style: { backgroundColor: valueColor } };
          } else {
            return { style: {} };
          }
        }}
      />
    );
  }, [props.outputReloads, props.viewRanks, minMaxVals]);

  return (
    <div style={{ width: "100%" }}>
      <Button onClick={() => props.setViewRanks(!props.viewRanks)}>
        {props.viewRanks ? "View Model Scores" : "View Ranks"}
      </Button>
      <br />
      <div className="pga-model-builder-stat-table-container">{tableMemo}</div>
    </div>
  );
}
